'use client'

import { useEffect, useState } from 'react'

interface ConfettiAnimationProps {
  isActive: boolean
  duration?: number
}

interface Confetti {
  id: number
  x: number
  y: number
  rotation: number
  scale: number
  color: string
  velocity: { x: number; y: number }
  rotationSpeed: number
}

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
  '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
]

export function ConfettiAnimation({ isActive, duration = 1500 }: ConfettiAnimationProps) {
  const [confetti, setConfetti] = useState<Confetti[]>([])
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (!isActive) return

    setIsAnimating(true)

    let nextId = 0

    const createParticle = (): Confetti => ({
      id: nextId++,
      x: Math.random() * window.innerWidth,
      y: -20 - Math.random() * 40,
      rotation: Math.random() * 360,
      scale: 0.5 + Math.random() * 0.5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      velocity: {
        x: (Math.random() - 0.5) * 3,
        y: Math.random() * 3 + 2
      },
      rotationSpeed: (Math.random() - 0.5) * 10
    })

    // Initial burst
    const initial: Confetti[] = []
    for (let i = 0; i < 150; i++) {
      initial.push(createParticle())
    }
    setConfetti(initial)

    let animationFrame: number
    const startTime = Date.now()
    let lastSpawn = startTime

    const animate = () => {
      const now = Date.now()
      const elapsed = now - startTime
      const spawning = elapsed < duration

      if (!spawning) {
        // After duration, let remaining particles fall then stop
        setConfetti(prev => {
          const updated = prev.map(particle => ({
            ...particle,
            x: particle.x + particle.velocity.x,
            y: particle.y + particle.velocity.y,
            rotation: particle.rotation + particle.rotationSpeed,
            velocity: {
              x: particle.velocity.x * 0.99,
              y: particle.velocity.y + 0.1
            }
          })).filter(p => p.y < window.innerHeight + 20)

          if (updated.length === 0) {
            setIsAnimating(false)
            return []
          }
          return updated
        })
        animationFrame = requestAnimationFrame(animate)
        return
      }

      // Spawn new particles continuously (~15 per frame)
      const newParticles: Confetti[] = []
      if (now - lastSpawn > 30) {
        for (let i = 0; i < 8; i++) {
          newParticles.push(createParticle())
        }
        lastSpawn = now
      }

      setConfetti(prev => [
        ...prev.map(particle => ({
          ...particle,
          x: particle.x + particle.velocity.x,
          y: particle.y + particle.velocity.y,
          rotation: particle.rotation + particle.rotationSpeed,
          velocity: {
            x: particle.velocity.x * 0.99,
            y: particle.velocity.y + 0.1
          }
        })).filter(p => p.y < window.innerHeight + 20),
        ...newParticles
      ])

      animationFrame = requestAnimationFrame(animate)
    }

    animationFrame = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(animationFrame)
    }
  }, [isActive, duration])

  if (!isAnimating) return null

  return (
    <div className="fixed inset-0 pointer-events-none z-[10000]">
      {confetti.map(particle => (
        <div
          key={particle.id}
          className="absolute w-3 h-3 rounded-sm"
          style={{
            left: particle.x,
            top: particle.y,
            transform: `rotate(${particle.rotation}deg) scale(${particle.scale})`,
            backgroundColor: particle.color,
            transition: 'none'
          }}
        />
      ))}
    </div>
  )
}
